import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type KeyboardEvent,
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { PlanStep, WorkPlan } from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import {
  awaitedSince,
  type CheckFinding,
  CheckResponseSchema,
  type DraftAgainOffer,
  DraftAgainOfferSchema,
  RefusalSchema,
} from "../src/draft-again-contract.ts";
import {
  IN_FLIGHT_CHIP,
  type InFlightView,
  openStep,
  withoutOpenWork,
} from "../src/in-flight-view.ts";
import { usePlanApi } from "./workspace-context.tsx";

/*
 * "Check this conversation" and "Draft again" (docs/2026-10-12-draft-again.md):
 * the last sheet of the Work panel, for a conversation whose draft or card looks out of date. The
 * server decides whether either is offered (`draft-again.ts`); where it offers neither, or the
 * workspace is off the switch, nothing is drawn. The check answers in one sentence. Draft again asks
 * first, in the sheet itself, because the draft on screen is replaced and the rep's edits to it are
 * not carried over. Neither has a key: a new draft is never one accidental keystroke away.
 *
 * Once a new attempt is asked for, the Work panel draws neither the draft nor the questions it
 * replaces, and says instead that a new draft is being written (`useRedraft`), until the attempt
 * ends. That is read from the server's offer, never from the click, so a reload says the same.
 *
 * The same offer says when a question on screen belongs to a run that is closed
 * (docs/2026-10-13-draft-again-limits-design.md). Such a card is drawn closed (`OpenQuestion`,
 * `useQuestionClosed`) and the sheet says what that means, because an answer saved to it would
 * resume nothing.
 */

/**
 * What a new attempt replaces on this conversation, so the confirmation can name it. Questions that
 * are already closed are not closed by it a second time.
 */
type Replaces = "draft" | "questions" | "closed_questions" | "nothing";

/** What the sheet last said: a check's finding, or why a request drafted nothing. */
type Said =
  | { kind: "finding"; finding: CheckFinding; draftAgain: boolean }
  | { kind: "refused"; sentence: string }
  | null;

/**
 * One sentence per finding, in the words the rest of the Work panel uses. A read that failed says it
 * could not check; it never says the reply is unanswered. `draftAgain` is whether the check offers a
 * new attempt, which only the ended request's sentence depends on.
 */
export function checkSentence(finding: CheckFinding, draftAgain = false): string {
  switch (finding) {
    case "answered":
      return "Someone already answered this message in Bison. Nothing to draft.";
    case "lead_wrote_again":
      return "The lead wrote again after this reply. Answer the newest message.";
    case "being_drafted":
      return "The app is still working on this reply, so a new draft cannot be asked for yet.";
    case "request_ended":
      return draftAgain
        ? `Nobody has answered this message in Bison yet. ${REQUEST_ENDED} You can ask again.`
        : `Nobody has answered this message in Bison yet. ${REQUEST_ENDED} ${NO_OTHER_ATTEMPT}`;
    case "stopped":
      return "Drafting stopped before anything was shown for this reply. Nothing has been sent.";
    case "unanswered":
      return "Nobody has answered this message in Bison yet.";
    case "could_not_check":
      return "This could not be checked just now. Nothing changed. Try again in a minute.";
  }
}

/** A new attempt that ended without a result: what the rep has is what was there before it. */
const REQUEST_ENDED =
  "The new draft that was asked for did not arrive. Nothing has been sent, and what is here has not changed.";
const NO_OTHER_ATTEMPT = "The app cannot start another for this reply.";

/** The same, as the sheet's standing line: it has the time the draft was asked for. */
function endedSentence(since: string | null, draftAgain: boolean, when: (at: string) => string) {
  const asked =
    since === null
      ? REQUEST_ENDED
      : `A new draft was asked for ${when(since)} and did not arrive. Nothing has been sent, and what is here has not changed.`;
  return draftAgain ? asked : `${asked} ${NO_OTHER_ATTEMPT}`;
}

/** A campaign's history is still coming in, so every change to its conversations is held (423). */
const IMPORTING = "Still importing this campaign's history";

/**
 * Why "Draft again" drafted nothing, in words the rep can act on; the route names the code. A
 * refusal can arrive after the question's run was closed for the attempt (`questionClosed`): the
 * one sentence that says nothing changed is then said without that claim.
 */
export function draftAgainRefusal(code: string | null, questionClosed = false): string {
  switch (code) {
    case "answered":
    case "current_turn_answered":
      return "Someone already answered this message in Bison, so no draft was asked for.";
    case "current_turn_sent":
      return "A reply to this message was already sent.";
    case "lead_wrote_again":
    case "plan_changed":
      return "The lead wrote again, so this reply can no longer be drafted. Answer the newest message.";
    case "could_not_check":
      return "Bison could not be read just now, so no draft was asked for. Try again in a minute.";
    case "temporal_unavailable":
      return "The app could not tell whether a draft is still being written, so no new one was asked for. Try again in a minute.";
    case "run_in_flight":
    case "effect_in_flight":
    case "notification_in_flight":
      return "The app is still working on this reply, so no new draft was asked for. Try again in a few minutes.";
    case "request_ended":
      return `${REQUEST_ENDED} ${NO_OTHER_ATTEMPT}`;
    case "saved_run_mismatch":
      return "What the app has saved for this conversation does not match the reply shown here, so no new draft was asked for. Check this conversation first.";
    case "drafting_disabled":
      return "Drafting is off for this conversation, so no draft was asked for.";
    case "current_turn_approved":
      return "This draft is already approved, so it was not replaced.";
    default:
      return questionClosed ? REFUSED_AFTER_CLOSURE : REFUSED_UNCHANGED;
  }
}

/** A refusal with no sentence of its own, where the conversation is as it was before the click. */
const REFUSED_UNCHANGED =
  "No new draft was asked for, and nothing changed. Check this conversation, then try again.";
/**
 * The same where the question is closed, by this request or before it: something may have changed,
 * so the sentence does not say that nothing did. That the question is closed, and what that means,
 * is the standing line's to say, beside it and once (`closedQuestionSentence`).
 */
const REFUSED_AFTER_CLOSURE =
  "No new draft was asked for. Check this conversation, then try again.";

/**
 * No answer came back, or not one the app can read: the request may or may not have been taken, so
 * the sentence claims neither and sends the rep to the check, which reads the record.
 */
export const UNCONFIRMED_REQUEST =
  "The app did not confirm this request. Check this conversation before asking again.";

/** What the confirmation warns of: what is replaced, and that the new attempt may not be a draft. */
function replacesSentence(replaces: Replaces): string {
  if (replaces === "draft")
    return "The current draft is set aside while a new one is written, and your edits to it are not carried over. The app reads the thread again, so it may ask a question instead of drafting.";
  if (replaces === "questions")
    return "The open questions close. The app asks again only what it still needs, and answers typed here are not carried over.";
  if (replaces === "closed_questions")
    return "The question above is already closed. The app reads this reply again: it drafts it, or asks again what it still needs.";
  return "The app reads this reply again and drafts it afresh.";
}

/** A question the rep could still be answering, as the plan stores it. */
const openQuestion = (step: PlanStep): boolean => step.input.kind === "question" && openStep(step);

/** What a new attempt would replace: an open draft first, then open questions. */
export function replacedBy(plan: Pick<WorkPlan, "steps">, questionClosed = false): Replaces {
  const open = plan.steps.filter(openStep);
  if (open.some((step) => step.input.kind === "reply")) return "draft";
  if (!open.some(openQuestion)) return "nothing";
  return questionClosed ? "closed_questions" : "questions";
}

/**
 * The sheet's standing line for a question whose run is closed, when no new draft is on its way:
 * what the card above is, that nothing went out, and the way on. Where a new draft cannot be asked
 * for, it says so instead of leaving the missing control to be wondered at. After the line about a
 * request that ended (`afterEnded`), which has already said that nothing was sent and whether
 * another can start, it says neither again.
 */
export function closedQuestionSentence(draftAgain: boolean, afterEnded = false): string {
  const closed = "The question above is closed, so it can no longer be answered.";
  const sent = afterEnded ? "" : " Nothing has been sent.";
  if (draftAgain)
    return `${closed}${sent} Draft again to have this reply read afresh: the app drafts it, or asks again what it still needs.`;
  return afterEnded ? closed : `${closed}${sent} A new draft cannot be asked for here right now.`;
}

/** A refusal and a check that could not run are announced at once; the rest waits its turn. */
const urgent = (said: Said): boolean =>
  said !== null && (said.kind === "refused" || said.finding === "could_not_check");
const sentence = (said: Exclude<Said, null>): string =>
  said.kind === "refused" ? said.sentence : checkSentence(said.finding, said.draftAgain);
/** The check's own sentence about an ended request, which the standing line would only repeat. */
const saysEnded = (said: Said): boolean =>
  said?.kind === "finding" && said.finding === "request_ended";

/**
 * A key held down repeats. On a button the repeat is another press, so Enter held on "Draft again"
 * would open the question and answer it. Only the first press of a key counts here.
 */
const firstPressOnly = (event: KeyboardEvent<HTMLButtonElement>): void => {
  if (event.repeat) event.preventDefault();
};

/** What the controls do, under the heading: only what is offered is named. */
const purpose = (offer: DraftAgainOffer): string =>
  offer.draftAgain
    ? "See whether someone already answered in Bison, or have the reply drafted again."
    : "See whether someone already answered in Bison.";

/**
 * What the sheet has to say, in two regions that are always in the page so that what arrives in
 * them is announced: politely for a wait, an ended request or a finding, at once for a refusal or
 * a failed check. A requested draft is either still awaited or ended, never both.
 */
function Announcements(props: {
  said: Said;
  offer: DraftAgainOffer;
  /** The question before a new attempt is open: it says itself what the closed question means. */
  confirming: boolean;
  /** A requested attempt's result has just been drawn above (`useArrival`). */
  arrived: string | null;
  when: (at: string) => string;
}) {
  const { said, offer, arrived } = props;
  // An offer kept from the run before can still name the wait the arrival ended.
  const waiting = awaitedSince(offer) !== null && arrived === null;
  return (
    <>
      <div role="status">
        {/* The card above says it in full, with its time; here, where it was asked, it says where. */}
        {waiting && (
          <p className="lcr-draft-again-said">
            Drafting again. The new draft appears above when it is ready.
          </p>
        )}
        {arrived && <p className="lcr-draft-again-said">{arrived}</p>}
        {offer.requestEnded && !saysEnded(said) && (
          <p className="lcr-draft-again-said">
            {endedSentence(offer.requestedSince, offer.draftAgain, props.when)}
          </p>
        )}
        {offer.questionClosed === true && !waiting && !props.confirming && (
          <p className="lcr-draft-again-said">
            {closedQuestionSentence(offer.draftAgain, offer.requestEnded)}
          </p>
        )}
        {said && !urgent(said) && <p className="lcr-draft-again-said">{sentence(said)}</p>}
      </div>
      <div role="alert">
        {said && urgent(said) && (
          <p className="lcr-draft-again-said lcr-draft-again-alert">{sentence(said)}</p>
        )}
      </div>
    </>
  );
}

/**
 * The question before a new attempt, in the sheet itself. The focus goes to the question, not to
 * its answer: the confirming button has the label of the button that opened it, and a key still
 * held down must find nothing under it to press.
 */
function Confirmation(props: {
  replaces: Replaces;
  disabled: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const question = useRef<HTMLFieldSetElement>(null);
  useEffect(() => question.current?.focus(), []);
  return (
    <fieldset
      ref={question}
      className="lcr-review-confirm lcr-draft-again-question"
      aria-labelledby="lcr-draft-again-question"
      aria-describedby="lcr-draft-again-consequence"
      tabIndex={-1}
    >
      <strong id="lcr-draft-again-question">Draft this reply again?</strong>
      <p id="lcr-draft-again-consequence">{replacesSentence(props.replaces)}</p>
      <div className="lcr-actions">
        <button
          type="button"
          disabled={props.disabled}
          onKeyDown={firstPressOnly}
          onClick={props.onConfirm}
        >
          Draft again
        </button>
        <button type="button" onKeyDown={firstPressOnly} onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </fieldset>
  );
}

/** The sheet with its data handed in: what is offered, what was last said, and what is running. */
export function DraftAgainSheet(props: {
  offer: DraftAgainOffer;
  /** A requested attempt's result has just been drawn above (`useArrival`). */
  arrived?: string | null;
  replaces: Replaces;
  said: Said;
  busy: "check" | "draft" | null;
  /** Another command of this conversation is running, or the conversation is read-only. */
  disabled: boolean;
  /** The rep has unsaved text in a draft, which a new attempt would not carry over. */
  editing: boolean;
  when: (at: string) => string;
  onCheck: () => void;
  onDraft: () => void;
}) {
  const [asked, setAsked] = useState(false);
  const sheet = useRef<HTMLElement>(null);
  const ask = useRef<HTMLButtonElement>(null);
  const { offer, said, busy } = props;
  const idle = busy === null && !props.disabled && !props.editing;
  // The question closes by itself when the server stops offering what it asks about.
  const confirming = asked && offer.draftAgain;
  if (!offer.check && said === null) return null;
  return (
    <article
      ref={sheet}
      className="lcr-sheet lcr-draft-again"
      aria-labelledby="lcr-draft-again-heading"
      aria-busy={busy !== null}
      tabIndex={-1}
    >
      <h3 id="lcr-draft-again-heading">Looks wrong or out of date?</h3>
      {offer.check && !confirming && <p className="lcr-meta-text">{purpose(offer)}</p>}
      <Announcements
        said={said}
        offer={offer}
        confirming={confirming}
        arrived={props.arrived ?? null}
        when={props.when}
      />
      {confirming && (
        <Confirmation
          replaces={props.replaces}
          disabled={!idle}
          onConfirm={() => {
            setAsked(false);
            // The question leaves the page with the focus in it: hand the focus to the sheet.
            sheet.current?.focus();
            props.onDraft();
          }}
          onCancel={() => {
            setAsked(false);
            // The focus returns to the control that opened the question, once it is drawn again.
            queueMicrotask(() => ask.current?.focus());
          }}
        />
      )}
      {!confirming && offer.check && (
        <div className="lcr-actions">
          <button type="button" disabled={!idle} onClick={props.onCheck}>
            {busy === "check" ? "Checking Bison…" : "Check this conversation"}
          </button>
          {offer.draftAgain && (
            <button
              ref={ask}
              type="button"
              disabled={!idle}
              onKeyDown={firstPressOnly}
              onClick={() => setAsked(true)}
            >
              {busy === "draft" ? "Asking for a new draft…" : "Draft again"}
            </button>
          )}
        </div>
      )}
      {props.editing && offer.check && (
        <p className="lcr-meta-text lcr-draft-again-hint">
          Save or discard your edit to use these.
        </p>
      )}
    </article>
  );
}

/** What the server offers for this conversation, or null where it offers nothing at all. */
async function readOffer(planApi: string): Promise<DraftAgainOffer | null> {
  const response = await fetch(`${planApi}/draft-again`, { credentials: "same-origin" });
  // 404: the workspace is off the switch, or this server has no such route. Nothing is drawn.
  if (!response.ok) return null;
  return DraftAgainOfferSchema.parse(await response.json());
}

/** The cache key of a conversation's offer; a refusal that shows the page is behind drops it. */
export const OFFER_QUERY = "draft-again";

/** The key of a "Draft again" still being asked, which the Work panel holds its controls for. */
const askingKey = (planId: string) => ["draft-again-request", planId];

/**
 * Whether a "Draft again" of this conversation is being asked: from the click until the server has
 * answered and the page has read the conversation again. Nothing is hidden for it yet, because the
 * request may still be refused, but nothing on the draft it may replace can be done either.
 */
export function useRedraftAsking(planId: string): boolean {
  return useIsMutating({ mutationKey: askingKey(planId) }) > 0;
}

/**
 * Whether the open conversation has been read since it was opened, as its pane reads it. Until then
 * the page draws what it kept: a copy minutes old, or one prefetched while the pointer rested on its
 * row, which cannot show a "Draft again" another rep has asked for since. True outside a pane.
 */
export const ConversationRead = createContext(true);

/**
 * Whether a command of the Work panel is running: one of its own, as the setter says, or a
 * "Draft again" being asked, which holds every control there as the panel's own commands do. Until
 * the conversation has been read since it was opened (`ConversationRead`) they wait as well, so the
 * draft a kept copy shows is not approved before the page knows it is still the one to send.
 */
export function useWorkBusy(planId: string): [boolean, (busy: boolean) => void] {
  const [running, setRunning] = useState(false);
  const asking = useRedraftAsking(planId);
  const read = useContext(ConversationRead);
  return [running || asking || !read, setRunning];
}

/** The Work panel while a new attempt is awaited (`useRedraft`). */
export type Redraft = {
  /** What the panel says in place of what the attempt replaces. */
  flight: InFlightView | null;
  /** The same as the panel's summary names it, before its open work: a phone shows only that. */
  chips: readonly { label: string }[];
  /** The plan as the panel draws it: while the attempt is awaited, without the open work it replaces. */
  plan: WorkPlan;
  /**
   * Whether the run's draft may be offered to the reply box as a starting point: not while it is
   * being replaced, nor before the conversation has been read since it was opened
   * (`ConversationRead`), when a kept copy cannot show that another rep has asked for a new one.
   */
  offersDraft: boolean;
};

/**
 * What a new attempt does to the Work panel. Awaited, the run's open work (its draft, its
 * questions, its other open steps) is not drawn and cannot be acted on, and a card says a new draft
 * is being written. It ends when the server says so: the new result shown (another run), or the
 * attempt ended without one, which brings back what was there.
 */
export function useRedraft(
  planId: string,
  /**
   * The conversation as its pane shows it; absent while the plan is still being read, when nothing
   * is awaited or offered (null). The pane keeps one component through both, so what it holds, the
   * reply box among it, is not drawn afresh when the plan lands.
   */
  shown: { plan: WorkPlan; redraftSince: string | null } | undefined,
  refresh: () => Promise<void>,
  /** An editor holds unsaved text: its work stays drawn, so the editor is never taken away. */
  editing: boolean,
): Redraft | null {
  // Before the plan is read there is no run to ask about: the offer's read waits for one.
  const since = useAwaitedSince(
    shown?.plan ?? { id: planId, revision: -1, runId: null },
    shown?.redraftSince ?? null,
    refresh,
    shown !== undefined,
  );
  const read = useContext(ConversationRead);
  if (!shown) return null;
  const { plan } = shown;
  if (since === null) return { flight: null, chips: [], plan, offersDraft: read };
  return {
    flight: { kind: "redrafting", since },
    chips: [IN_FLIGHT_CHIP.redrafting],
    plan: editing ? plan : withoutOpenWork(plan),
    offersDraft: false,
  };
}

/**
 * When the awaited attempt was asked for, read from the offer of the run on screen. A request
 * names the run it replaces, so once the new result is shown on the server the offer stops naming
 * a wait, and it can say so before this page has read the new plan: the two are separate reads.
 * Then the wait is kept, and the conversation read again at once, so the old draft never comes
 * back in between. Read for another run, an offer says nothing about this one (`same_run`). Until
 * this page has read the offer itself, which asks the workflow host, the conversation's own mark
 * stands (`mark`, sent with it where the switch is on), so the old draft is not offered meanwhile:
 * a first read still on its way, an offer kept from before the request, one that failed.
 */
function useAwaitedSince(
  plan: Shown,
  mark: string | null,
  refresh: () => Promise<void>,
  enabled: boolean,
): string | null {
  const read = useOffer(plan, enabled, "same_run");
  const offer = read.data;
  // A read that failed says nothing about the wait, whether it threw or answered null (`readOffer`).
  const heard = read.status === "success" && read.isFetchedAfterMount && !!offer;
  const since = awaitedSince(offer);
  const [seen, setSeen] = useState<{ runId: string | null; since: string } | null>(null);
  if (since !== null && (seen?.runId !== plan.runId || seen.since !== since))
    setSeen({ runId: plan.runId, since });
  const behind =
    since === null && offer?.requestEnded === false && seen?.runId === plan.runId ? seen : null;
  const latest = useRef(refresh);
  latest.current = refresh;
  useEffect(() => {
    if (!behind) return;
    void Promise.allSettled([latest.current()]).then(() => setSeen(null));
  }, [behind]);
  return since ?? behind?.since ?? (heard ? null : mark);
}

/** The conversation as an offer is read for it: which one, at which revision, showing which run. */
type Shown = Pick<WorkPlan, "id" | "revision" | "runId">;

/**
 * The offer for the conversation on screen. The sheet and each question card read it through this
 * one query, so a conversation is asked about once however many of them are drawn.
 *
 * While a changed conversation is read again the sheet keeps what it last knew. A question card
 * keeps it only where it was read about the run the card shows (`same_run`): a closed run stays
 * closed, but a new attempt that asks the same question again is another run, and its card must
 * not be drawn closed from the offer of the run it replaced.
 */
function useOffer(plan: Shown, enabled = true, keeps: "always" | "same_run" = "always") {
  const planApi = usePlanApi(plan.id);
  return useQuery({
    queryKey: [OFFER_QUERY, plan.id, plan.revision, plan.runId],
    queryFn: () => readOffer(planApi),
    enabled,
    placeholderData: (previous, read) =>
      keeps === "always" || read?.queryKey[3] === plan.runId ? previous : undefined,
    // While a new attempt is awaited, look again on the page's own cadence; an ended one is not.
    refetchInterval: (query) =>
      query.state.data?.requestedSince && !query.state.data.requestEnded ? 15_000 : false,
  });
}

/**
 * Whether this step is a question whose run is closed. False wherever the server does not say so:
 * a workspace off the switch, a server before the field, an offer that could not be read. The card
 * is then drawn as it always was, and the answers route still refuses a closed one by name.
 */
export function useQuestionClosed(plan: Shown, step: PlanStep) {
  return useClosedQuestionOffer(plan, step) !== null;
}

/** The offer that says this step is a question whose run is closed, or null where none does. */
function useClosedQuestionOffer(plan: Shown, step: PlanStep) {
  const open = openQuestion(step);
  const offer = useOffer(plan, open, "same_run").data;
  return open && offer?.questionClosed === true ? offer : null;
}

/**
 * A question card's answer form, or, where the question's run is closed, what was asked and why it
 * can no longer be answered. A form whose save the server would refuse is not offered.
 */
export function OpenQuestion(props: { plan: Shown; step: PlanStep; children: ReactNode }) {
  const closed = useClosedQuestionOffer(props.plan, props.step);
  if (!closed || props.step.input.kind !== "question") return props.children;
  return (
    <div className="lcr-question-closed">
      <ClosedQuestions prompts={props.step.input.questions.map((question) => question.prompt)} />
      {/* The sheet is drawn only where a check is offered; the card points at it only then. */}
      <p className="lcr-meta-text">
        {closed.check ? `${CLOSED_QUESTION_NOTE} ${SEE_SHEET}` : CLOSED_QUESTION_NOTE}
      </p>
    </div>
  );
}

/** What was asked, still readable: one question as a sentence, several as a list. */
function ClosedQuestions(props: { prompts: readonly string[] }) {
  const [only] = props.prompts;
  if (props.prompts.length === 1) return <p className="lcr-question-closed-asked">{only}</p>;
  return (
    <ul className="lcr-question-closed-asked">
      {props.prompts.map((prompt) => (
        <li key={prompt}>{prompt}</li>
      ))}
    </ul>
  );
}

/**
 * Under a closed question, in the card, whose badge already says "Closed": why there is no form, and
 * where the next step is. True whether a new draft is awaited, was refused or was never asked for.
 */
const CLOSED_QUESTION_NOTE =
  "The app is no longer waiting for this answer, so it cannot be saved here.";
const SEE_SHEET = "What happens next is under “Looks wrong or out of date?” below.";

/**
 * How long the page waits for each answer. The server bounds every read it makes (`draft-again.ts`):
 * 10 seconds for each answer of the workflow host, 20 for the Bison read, 20 for a settle, and 45 for
 * the one answer that closes a run waiting on questions. A check can take 70 seconds at the worst,
 * and the page outwaits it, so "nothing changed" is never said while the server may still settle. A
 * new attempt can take longer still; a page that gives up on one says the request was not confirmed,
 * which stays true whatever the server then does.
 */
const WAIT_MS = { check: 90_000, draft: 120_000 } as const;

const post = (path: string, waitMs: number): Promise<Response> =>
  fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: "{}",
    signal: AbortSignal.timeout(waitMs),
  });

const COULD_NOT_CHECK: Said = { kind: "finding", finding: "could_not_check", draftAgain: false };

/**
 * What the sheet says after a request. An accepted "Draft again" says nothing here: the waiting line
 * comes from the record. A check the server no longer offers says nothing either, because the
 * conversation moved on and the page is about to show how. Only a 409 is the server's word that
 * nothing was asked for; any other failure of "Draft again" is an outcome the page does not know.
 */
async function saidBy(action: "check" | "draft", response: Response): Promise<Said> {
  if (response.ok)
    return action === "check"
      ? { kind: "finding", ...CheckResponseSchema.parse(await response.json()) }
      : null;
  const failure = RefusalSchema.safeParse(await response.json().catch(() => null));
  const code = failure.success ? failure.data.error : null;
  // 423: the campaign's history is still coming in. Nothing was read or asked for, and a minute
  // will not change that.
  if (response.status === 423)
    return {
      kind: "refused",
      sentence:
        action === "check"
          ? `${IMPORTING}, so this cannot be checked yet. Nothing changed. Try again when it finishes.`
          : `${IMPORTING}, so no new draft was asked for. Try again when it finishes.`,
    };
  if (action === "check") return code === "not_offered" ? null : COULD_NOT_CHECK;
  const questionClosed = failure.success && failure.data.questionClosed === true;
  return {
    kind: "refused",
    sentence:
      response.status === 409 ? draftAgainRefusal(code, questionClosed) : UNCONFIRMED_REQUEST,
  };
}

/**
 * The sentence that says a requested attempt's result is drawn, for the run it arrived as. The card
 * and the waiting line leave the page with the wait, which a screen reader does not announce; this
 * is said once, politely, in the sheet where the request was made, and goes with the next run.
 */
function useArrival(plan: WorkPlan, offer: DraftAgainOffer | null | undefined): string | null {
  // The run a wait was seen for, and the run that came after it.
  const [waited, setWaited] = useState<string | null>(null);
  const [arrivedAs, setArrivedAs] = useState<string | null>(null);
  if (waited !== null && plan.runId !== waited) {
    setArrivedAs(plan.runId);
    setWaited(null);
  } else if (waited !== null && offer?.requestEnded === true)
    // Ended with nothing shown: the next run is the lead's, not the one asked for.
    setWaited(null);
  else if (awaitedSince(offer) !== null && plan.runId !== arrivedAs && plan.runId !== waited)
    setWaited(plan.runId);
  if (arrivedAs === null || arrivedAs !== plan.runId) return null;
  return replacedBy(plan) === "draft"
    ? "The new draft is ready above."
    : "Drafting again has finished. What came of it is above.";
}

/**
 * What the Work panel needs to place the sheet: whether the server offers it at all, and whether the
 * offer itself says something is wrong (a closed question, a requested draft awaited or ended). A
 * healthy conversation keeps the sheet folded under More actions (Dawid, 2026-10-21, ledger `e4dda67e`).
 */
export function useDraftAgainOffer(plan: Shown): { offered: boolean; needsYou: boolean } {
  const offer = useOffer(plan).data;
  return {
    offered: offer?.check === true,
    needsYou:
      offer?.questionClosed === true ||
      awaitedSince(offer) !== null ||
      offer?.requestEnded === true,
  };
}

/** The sheet with its data, for the conversation on screen. */
export function DraftAgain(props: {
  plan: WorkPlan;
  disabled: boolean;
  editing: boolean;
  /** Behind the Work panel's More actions: drawn only once it has something of its own to say. */
  folded: boolean;
  when: (at: string) => string;
  onChange: () => Promise<void>;
}) {
  const planApi = usePlanApi(props.plan.id);
  const cache = useQueryClient();
  const [busy, setBusy] = useState<"check" | "draft" | null>(null);
  /*
   * What was said, with the run it was said about. Once the conversation shows another run (a new
   * attempt arrived, or the lead wrote again) it is stale and is not shown.
   */
  const runId = props.plan.runId;
  const [spoken, setSpoken] = useState<{ runId: string | null; said: Said }>({ runId, said: null });
  const said = spoken.runId === runId ? spoken.said : null;
  const setSaid = (next: Said): void => setSpoken({ runId, said: next });
  // One request at a time, whatever the clicks: the label changes a render later than the click.
  const running = useRef(false);
  const offer = useOffer(props.plan);
  const arrived = useArrival(props.plan, offer.data);
  const run = async (action: "check" | "draft"): Promise<void> => {
    if (running.current) return;
    running.current = true;
    setBusy(action);
    setSaid(null);
    try {
      const path = action === "check" ? "check-conversation" : "draft-again";
      setSaid(await saidBy(action, await post(`${planApi}/${path}`, WAIT_MS[action])));
    } catch {
      // Offline, timed out, or an answer the page could not read.
      setSaid(
        action === "check" ? COULD_NOT_CHECK : { kind: "refused", sentence: UNCONFIRMED_REQUEST },
      );
    }
    // Read the conversation again whatever happened: a request that timed out may have been taken.
    // A failed re-read must not replace what the request itself just said.
    await Promise.allSettled([
      cache.invalidateQueries({ queryKey: [OFFER_QUERY, props.plan.id] }),
      props.onChange(),
    ]);
    running.current = false;
    setBusy(null);
  };
  // Asked as a mutation so that the Work panel can hold the draft it may replace (`useRedraftAsking`).
  const asking = useMutation({
    mutationKey: askingKey(props.plan.id),
    mutationFn: () => run("draft"),
  });
  if (!offer.data) return null;
  if (props.folded && said === null && busy === null && arrived === null) return null;
  return (
    <DraftAgainSheet
      offer={offer.data}
      arrived={arrived}
      replaces={replacedBy(props.plan, offer.data.questionClosed === true)}
      said={said}
      busy={busy}
      disabled={props.disabled}
      editing={props.editing}
      when={props.when}
      onCheck={() => void run("check")}
      onDraft={() => asking.mutate()}
    />
  );
}
